import React, { useState, useEffect, useRef } from 'react';
import { SignedIn, SignedOut, RedirectToSignIn, useAuth } from '@clerk/clerk-react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext.jsx';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { ApiClient } from '../utils/apiClient.js';
import Step2VoiceConfig from '../components/Step2VoiceConfig.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import WizardProgressIndicator from '../components/WizardProgressIndicator.jsx';
import WizardBreadcrumbs from '../components/WizardBreadcrumbs.jsx';
import WizardNavigationConfirmDialog from '../components/WizardNavigationConfirmDialog.jsx';
import { useWizardNavigation } from '../hooks/useWizardNavigation.js';
import { useWizardSessionManager } from '../utils/wizardSessionManager.js';

/**
 * Parrot Wizard Step 2 Page
 * Handles voice selection, creativity level, and voice settings configuration
 */
export default function ParrotWizardStep2() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // For editing mode
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();
  const apiClient = useRef(new ApiClient(getToken)).current;
  
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [step1Data, setStep1Data] = useState({});

  // TASK 12: Initialize session manager for KB configuration handling
  const sessionManager = useWizardSessionManager(
    step1Data.session_id,
    id, // parrotId for editing mode
    getToken
  );

  // Wizard navigation with unsaved changes protection
  const wizardNavigation = useWizardNavigation({
    sessionId: step1Data.session_id,
    parrotId: id,
    currentStep: 2,
    onSaveChanges: async () => {
      // Save current form data before navigation
      await wizardNavigation.saveCurrentStep(formData, { completed: false });
    },
    autoSave: false
  });

  // Check if we're in editing mode and load data
  useEffect(() => {
    if (id) {
      setIsEditing(true);
    }

    // Get step 1 data from navigation state or load from session
    if (location.state?.step1Data) {
      setStep1Data(location.state.step1Data);
      
      // Store session ID in localStorage if it exists
      if (location.state.step1Data.session_id) {
        localStorage.setItem('wizard_session_id', location.state.step1Data.session_id);
      }
      
      loadStep2Data();
    } else {
      // If no step 1 data, redirect back to step 1
      const step1Route = isEditing ? `/parrots/${id}/edit/step1` : '/parrots/new/step1';
      navigate(step1Route);
    }
  }, [id, location.state]);

  /**
   * Load existing step 2 data for editing or from session
   */
  const loadStep2Data = async () => {
    try {
      setIsLoading(true);
      
      if (isEditing && id) {
        // Load existing parrot voice data
        const existingData = {
          voice_id: 'default-voice-1',
          creativity_level: 'creative',
          stability: 'medium',
          similarity_boost: 'medium',
          style: 'medium'
        };
        setFormData(existingData);
      } else {
        // Set default values for new parrot
        setFormData({
          voice_id: '',
          creativity_level: 'creative',
          stability: 'medium',
          similarity_boost: 'medium',
          style: 'medium'
        });
      }
    } catch (error) {
      console.error('Failed to load step 2 data:', error);
      showToast('Failed to load voice configuration', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle form data changes
   */
  const handleFormDataChange = (newData) => {
    setFormData(newData);
    // Clear errors when data changes
    setErrors({});
  };

  /**
   * Handle proceeding to Step 3
   */
  const handleNext = async (stepData) => {
    try {
      setIsLoading(true);
      setErrors({});

      // Save step 2 data to backend wizard session
      const wizardResponse = await apiClient.post('/api/parrots/wizard/step2', {
        ...stepData,
        session_id: step1Data.session_id
      });
      
      if (!wizardResponse.success) {
        throw new Error(wizardResponse.error?.message || 'Failed to save step 2 data');
      }
      
      // Add the session_id to the step data for the next step
      const stepDataWithSession = {
        ...stepData,
        session_id: step1Data.session_id
      };
      
      // Navigate to step 3 with combined data
      const nextRoute = isEditing ? `/parrots/${id}/edit/step3` : '/parrots/new/step3';
      navigate(nextRoute, { 
        state: { 
          step1Data,
          step2Data: stepDataWithSession,
          isEditing,
          parrotId: id 
        } 
      });
      
    } catch (error) {
      console.error('Failed to proceed to step 3:', error);
      showToast('Failed to save progress. Please try again.', 'error');
      setErrors({ general: 'Failed to save progress. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle save and exit
   */
  const handleSaveAndExit = async (stepData) => {
    try {
      setIsLoading(true);
      setErrors({});

      // Save parrot with current configuration (without Step 3)
      const combinedData = {
        ...step1Data,
        ...stepData
      };

      console.log('Saving parrot with data:', combinedData);

      // Get session ID from session manager
      const sessionId = sessionManager?.sessionId;
      if (!sessionId) {
        throw new Error('No wizard session found');
      }

      // Save Step 2 data and mark as completed, then finalize (same pattern as Step 3)
      console.log('Saving Step 2 data and marking as completed...');
      const step2Response = await apiClient.post('/api/parrots/wizard/step2', {
        session_id: sessionId,
        ...stepData
      });

      if (!step2Response.success) {
        throw new Error(step2Response.error || 'Failed to save Step 2 data');
      }

      // Attempt to persist Step 1 knowledge base configuration to the wizard session
      try {
        const step1State = wizardNavigation.loadStepData ? wizardNavigation.loadStepData(1) : null;
        const step1FromState = step1State || step1Data || {};
        const kb = step1FromState.knowledge_base;
        const useKb = step1FromState.use_knowledge_base;

        if (useKb && kb) {
          const hasUrl = typeof kb.url === 'string' && kb.url.trim();
          const validTempFiles = Array.isArray(kb.files)
            ? kb.files
                .filter(f => f && f.temp_id && f.name && typeof f.size === 'number' && (f.mime_type || f.type))
                .map(f => {
                  const file = {
                    // Provide an id that the backend validator recognizes as temp
                    id: `temp_${f.temp_id}`,
                    name: f.name,
                    type: 'file',
                    size: f.size > 0 ? f.size : 1, // ensure positive size
                    mime_type: f.mime_type || f.type,
                    temp_r2_key: f.temp_r2_key || f.r2_key || null
                  };
                  return file;
                })
            : [];

          // Include existing selected files (global KB files) as well as temp uploads
          const existingSelected = Array.isArray(kb.files)
            ? kb.files
                .filter(f => f && f.id && typeof f.id === 'string' && !f.id.startsWith('temp_'))
                .map(f => ({
                  id: f.id,
                  name: f.name,
                  // Validator expects a MIME type
                  type: f.mime_type || f.type || 'application/pdf'
                }))
            : [];

          const filesPayload = [...existingSelected, ...validTempFiles];

          // Build URLs payload as an array, if a single URL is present
          const urlsPayload = hasUrl ? [{ url: kb.url.trim() }] : [];

          // Knowledge base files will be assigned automatically by the backend during step2/step3 save
          // No need to call queue APIs directly - backend handles assignment internally
          if (urlsPayload.length > 0 || filesPayload.length > 0) {
            console.log('Knowledge base files will be assigned automatically during save');
          }
        }
      } catch (kbErr) {
        console.warn('Knowledge base session sync skipped/failed:', kbErr);
      }

      console.log('Step 2 completed, now finalizing...');

      // TASK 12: Prepare finalization data with knowledge base configuration
      const finalizeData = {
        session_id: sessionManager?.sessionId
      };

      // TASK 12: Add knowledge base configuration if session manager is available
      if (sessionManager) {
        try {
          const step1Data = sessionManager.getState()?.step1Data || {};
          const kbEnabled = step1Data.use_knowledge_base || false;
          const finalizedKbConfig = sessionManager.prepareKnowledgeBaseForFinalization(isEditing, kbEnabled);
          
          finalizeData.knowledge_base_config = finalizedKbConfig;
          
          console.log('TASK 12: Knowledge base configuration prepared for Step 2 finalization:', {
            enabled: kbEnabled,
            filesCount: finalizedKbConfig.files?.length || 0,
            urlsCount: finalizedKbConfig.urls?.length || 0,
            mode: finalizedKbConfig.metadata?.finalization_mode
          });
        } catch (kbError) {
          console.warn('TASK 12: Failed to prepare KB configuration for Step 2 finalization:', kbError);
        }
      }

      // Now call finalize which should follow the normal path since step2_completed = 1
      const response = await apiClient.post('/api/parrots/wizard/finalize', finalizeData);

      if (!response.success) {
        throw new Error(response.error || 'Failed to save parrot');
      }
      
      showToast('Parrot saved successfully!', 'success');

      // Invalidate related caches so attachments reflect immediately
      try {
        const idNum = parseInt(id || response.parrot_id, 10);
        const parrotIdPath = isFinite(idNum) ? `/${idNum}` : '';
        apiClient.invalidateCaches([
          '/api/parrots',
          `/api/parrots${parrotIdPath}`,
          `/api/parrots${parrotIdPath}/wizard-data`,
          `/api/parrots${parrotIdPath}/knowledge-base-status`,
          `/api/parrots${parrotIdPath}/knowledge-base`,
          '/api/knowledge-base',
          '/api/knowledge-base/usage-stats',
          '/api/knowledge-base/sync-status'
        ]);
        
        // Add a delay to ensure database operations are committed and visible
        // then force a fresh fetch of the parrot knowledge base status
        setTimeout(async () => {
          try {
            if (isFinite(idNum)) {
              // Invalidate the cache again and force a fresh fetch
              apiClient.invalidateCache(`/api/parrots/${idNum}/knowledge-base-status`);
              await apiClient.get(`/api/parrots/${idNum}/knowledge-base-status`);
              console.log('Forced refresh of parrot knowledge base status after wizard completion');
            }
          } catch (refreshError) {
            console.warn('Failed to refresh parrot knowledge base status:', refreshError);
          }
        }, 2000); // 2 second delay to ensure DB operations are committed and visible
      } catch (_) {}

      // Clear the session
      localStorage.removeItem('wizard_session_id');

      navigate('/parrots');
      
    } catch (error) {
      console.error('Failed to save parrot:', error);
      showToast('Failed to save parrot. Please try again.', 'error');
      setErrors({ general: 'Failed to save parrot. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle going back to Step 1
   */
  const handleBack = () => {
    // Use wizard navigation to handle unsaved changes
    wizardNavigation.navigateToStep(1);
  };

  return (
    <>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
      <SignedIn>
        <div className="max-w-4xl mx-auto">
          {/* Navigation Breadcrumbs */}
          <WizardBreadcrumbs 
            currentStep={2} 
            isEditing={isEditing} 
            parrotId={id}
            className="mb-6" 
          />

          {/* Progress Indicator */}
          <WizardProgressIndicator currentStep={2} />

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {isEditing ? 'Edit Parrot - Step 2' : 'Create New Parrot - Step 2'}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
              Configure your parrot's voice and speaking style
            </p>
          </div>

          {/* Error Display */}
          {errors.general && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
              <p className="font-medium">Error</p>
              <p className="text-sm">{errors.general}</p>
            </div>
          )}

          {/* Loading State */}
          {isLoading && !formData.voice_id && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="text-center py-12">
                <LoadingSpinner size="lg" />
                <p className="text-gray-600 dark:text-gray-400 mt-4 dark:text-gray-300 dark:text-gray-600">Loading voice configuration...</p>
              </div>
            </div>
          )}

          {/* Step 2 Component */}
          {(!isLoading || formData.voice_id !== undefined) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <Step2VoiceConfig
                formData={formData}
                onFormDataChange={handleFormDataChange}
                onNext={handleNext}
                onSaveAndExit={handleSaveAndExit}
                onBack={handleBack}
                errors={errors}
                isLoading={isLoading}
              />
            </div>
          )}
        </div>

        {/* Navigation Confirmation Dialog */}
        <WizardNavigationConfirmDialog
          isOpen={wizardNavigation.showConfirmDialog}
          onConfirm={wizardNavigation.handleContinueWithoutSaving}
          onCancel={wizardNavigation.handleCancelNavigation}
          onSaveAndContinue={wizardNavigation.handleSaveAndContinue}
          targetStep={wizardNavigation.navigationIntent?.targetStep}
          targetStepName={wizardNavigation.navigationIntent?.targetStepName}
          changesSummary={wizardNavigation.getChangesSummary()}
          isLoading={wizardNavigation.isLoading}
        />
      </SignedIn>
    </>
  );
}
